import { paths, components } from '@/types/api';
import { fetchClient } from '@/util/fetchClient';

type CreateInquiryRequest = components['schemas']['CreateInquiryRequest'];

/**
 * Create inquiry payload.
 * Callers may omit `anonymousFanOut` so the API applies its default.
 * The generated OpenAPI type still marks the wire field as required.
 */
export type CreateInquiryV2Body = Omit<CreateInquiryRequest, 'anonymousFanOut'> & {
  anonymousFanOut?: boolean;
};

export type CreateInquiryV2Response =
  paths['/v2/inquiries']['post']['responses']['201']['content']['application/json'];

/**
 * Create a new inquiry and submit it to one or more experts
 *
 * @description Creates inquiries for up to 3 experts total with one primary and up to two additional.
 * Each expert receives a separate conversation thread. The first expert becomes the parent
 * inquiry with subsequent experts linked as child inquiries.
 *
 * @param body - The inquiry data including message, expertIds, and patient info.
 *   Directory inquiry from `/directory/inquiry/[id]` includes `expertId` for the primary expert plus `expertIds` with the primary first and any additional experts. The API requires `expertId` to match the first `expertIds` entry.
 *   Standalone inquiry from `/inquiry` includes only `expertIds` for all selected experts and omits `expertId`.
 * @returns Promise with the created inquiry data
 */
export async function createInquiryV2(
  body: CreateInquiryV2Body
): Promise<CreateInquiryV2Response['data']> {
  const { anonymousFanOut, ...rest } = body;

  const wireBody: CreateInquiryRequest =
    anonymousFanOut === undefined ? (rest as CreateInquiryRequest) : { ...rest, anonymousFanOut };

  const { data } = await fetchClient.POST('/v2/inquiries', {
    body: wireBody,
  });

  return data?.data;
}
